import { Approval } from "@mui/icons-material";
import { DialogTitle, Typography, DialogContent, DialogActions, Stack, Button, Grid2 as Grid, Checkbox, FormControlLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog } from "@mui/material";
import moment, { Moment } from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { useGetTagsBasedOnCanonicalNames } from "../../hooks";
import { SelectBoxOption } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { useLazyGetUsersQuery, useSetAccessStateMutation } from "../../redux/services";
import { openBackdrop, closeBackdrop } from "../../redux/slices/backdrop";
import { closeDialog } from "../../redux/slices/dialog";
import { DependencyChildren, StateManagement } from "../../screens/materials/dashboard";
import { Transition } from "../../screens/viewBOM/basicDetails";
import { DatePicker } from "../datePicker";
import { SelectBox } from "../reactSelect";
import { TextField } from "../textField";

const PublishReviewTable = ({ sequence_id, status, tableData }: { sequence_id: string; status: SelectBoxOption | null, tableData: { role: string; name: string; date: string }[] }) => {
    return (
        <TableContainer
            component={Paper}
            sx={{
                maxWidth: 600,
                backgroundColor: '#f8f0ff',
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid rgba(128, 0, 255, 0.1)',
                    color: '#5f3dc4',
                    padding: '8px 16px',
                }
            }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {status &&
                            <TableCell colSpan={3} sx={{ borderBottom: 'none', fontWeight: 'bold' }}>
                                {status.label}
                            </TableCell>
                        }
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={3} sx={{ borderBottom: 'none', fontWeight: 'bold' }}>
                            PROJ.NO: {sequence_id}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Sign</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableData.map((row) => (
                        <TableRow
                            sx={{
                                bgcolor: "#ffffff",
                                '& .MuiTableCell-root': {
                                    padding: 2
                                },
                                '& .MuiTableCell-root:nth-child(n+2)': {
                                    color: '#000000'
                                }
                            }}
                            key={row.role}
                        >
                            <TableCell>{row.role}</TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.date}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell>Color Code</TableCell>
                        <TableCell>Red (Change/Add)</TableCell>
                        <TableCell>Blue (Deletion)</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

interface IPublishState {
    reason: string;
    status: SelectBoxOption | null;
    creator: SelectBoxOption | null;
    reviewer: SelectBoxOption | null;
    approver: SelectBoxOption | null;
    createdDate: Moment;
    reviewedDate: Moment;
    approvedDate: Moment;
    dsChecked: boolean;
}

export const EntityPublish = (props: { isDocument?: boolean; canonical_name: string; _id: string; sequence_id: string; object_id: string; object_type: StateManagement; refetch?: () => void; }) => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const { options, loading } = useGetTagsBasedOnCanonicalNames(props.canonical_name);

    const [publishEntity] = useSetAccessStateMutation();
    const [getCreators, creatorResponse] = useLazyGetUsersQuery();
    const [getReviewers, reviewersResponse] = useLazyGetUsersQuery();
    const [getApprovers, approversResponse] = useLazyGetUsersQuery();

    const [open, setOpen] = React.useState(false);

    const [publishState, setState] = React.useState<IPublishState>({
        reason: "",
        status: null,
        creator: null,
        createdDate: moment(),
        reviewer: null,
        reviewedDate: moment(),
        approver: null,
        approvedDate: moment(),
        dsChecked: false,
    });

    const tableData = React.useMemo(() => {
        const { creator, createdDate, reviewer, reviewedDate, approver, approvedDate } = publishState;
        return [
            { role: 'Created By', name: creator?.label || "", date: moment(createdDate).format("DD/MM/YY") },
            { role: 'Reviewed By', name: reviewer?.label || "", date: moment(reviewedDate).format("DD/MM/YY") },
            { role: 'Approved By', name: approver?.label || "", date: moment(approvedDate).format("DD/MM/YY") },
        ];
        // eslint-disable-next-line
    }, [JSON.stringify(publishState)]);

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        setOpen(false);
    };

    const updateState = (key: keyof typeof publishState, value: any) => {
        setState((prev) => ({ ...prev, [key]: value }))
    }

    const loadCreators = async (search: string, loadedOptions: any, additional: any) => {
        try {
            const page = additional?.page || 0; // Ensure the page number is correctly retrieved
            let options: { data: any[] } = { data: [] };

            options = await getCreators({ search, page, pageSize: 50 }).unwrap();

            return {
                options: options.data
                    ?.filter((op) => !op.released)
                    .map((_: any) => ({
                        ..._,
                        value: _.user_id,
                        label: _.name,
                    })),
                hasMore: options.data.length === 50, // Ensuring pagination stops when necessary
                additional: { page: page + 1 }, // Increment page number for the next request
            };
        } catch (error) {
            console.error("error:", error);
            return { options: [], hasMore: false };
        }
    };

    const loadReviewers = async (search: string, loadedOptions: any, additional: any) => {
        try {
            const page = additional?.page || 0; // Ensure the page number is correctly retrieved
            let options: { data: any[] } = { data: [] };

            options = await getReviewers({ search, page, pageSize: 50 }).unwrap();

            return {
                options: options.data
                    ?.filter((op) => !op.released)
                    .map((_: any) => ({
                        ..._,
                        value: _.user_id,
                        label: _.name,
                    })),
                hasMore: options.data.length === 50, // Ensuring pagination stops when necessary
                additional: { page: page + 1 }, // Increment page number for the next request
            };
        } catch (error) {
            console.error("error:", error);
            return { options: [], hasMore: false };
        }
    };

    const loadApprovers = async (search: string, loadedOptions: any, additional: any) => {
        try {
            const page = additional?.page || 0; // Ensure the page number is correctly retrieved
            let options: { data: any[] } = { data: [] };

            options = await getApprovers({ search, page, pageSize: 50 }).unwrap();

            return {
                options: options.data
                    ?.filter((op) => !op.released)
                    .map((_: any) => ({
                        ..._,
                        value: _.user_id,
                        label: _.name,
                    })),
                hasMore: options.data.length === 50, // Ensuring pagination stops when necessary
                additional: { page: page + 1 }, // Increment page number for the next request
            };
        } catch (error) {
            console.error("error:", error);
            return { options: [], hasMore: false };
        }
    };

    const publish = async (row: any, publishState: IPublishState) => {
        const payload = {
            access_state: "release",
            notes: publishState.reason?.trim() ? publishState.reason : `publish the ${props.object_type}`,
            object_id: props?._id,
            object_type: props.object_type
        }
        dispatch(openBackdrop(`Publishing ${props.object_type} ${row?.sequence_id}....`))
        await publishEntity({ payload }).unwrap().then((res) => {
            if (res?._id) {
                enqueueSnackbar(res?.message ?? `${props.object_type?.toUpperCase()} published successfully!`, { variant: "success" });
                if (row.refetch) {
                    row.refetch();
                }
            }
        }).catch((error: any) => {
            console.error(`Error publishing ${props.object_type} ${row?.sequence_id}: ${error}`)
            let errorMessage: string = error?.data?.title ?? error?.title ?? `Error publishing ${props.object_type} ${row?.sequence_id}`
            enqueueSnackbar(errorMessage, { variant: "error" });
        }).finally(() => {
            dispatch(closeDialog())
            dispatch(closeBackdrop())
        })
    }

    return (<>
        {/* Title */}
        <DialogTitle>
            <Typography
                variant="h6"
                color="textPrimary"
                sx={{
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    width: "100%", fontFamily: 'htrts_medium'
                }}
            >
                Publish Operation
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 5 }}>
                    <Stack gap={3}>
                        <Button variant="outlined" sx={{ maxWidth: 250 }} onClick={handleOpen}>Check For Dependencies</Button>
                        <TextField
                            size="small"
                            label={"Reason for Publishing"}
                            id="publish-reason"
                            fullWidth
                            autoFocus
                            value={publishState.reason}
                            onChange={(event) => {
                                event.preventDefault();
                                updateState("reason", event.target.value)
                            }}
                            placeholder={"Enter the reason"}
                        />

                        <SelectBox
                            label={"Status"}
                            loading={loading}
                            options={options}
                            value={publishState.status || null}
                            onChange={(dat: SelectBoxOption) => {
                                updateState("status", dat)
                            }}
                            isMenuFixed
                        />

                        <Stack gap={4}>
                            <SelectBox
                                isPaginate
                                label={"Creator"}
                                loadOptions={loadCreators}
                                loading={creatorResponse.isLoading || creatorResponse.isFetching}
                                options={[]}
                                value={publishState.creator || null}
                                onChange={(dat: SelectBoxOption) => {
                                    updateState("creator", dat)
                                }}
                                isMenuFixed
                            />

                            <DatePicker
                                value={publishState.createdDate ? moment(publishState.createdDate, 'YYYY-MM-DD') : null}
                                onChange={(date) => {
                                    updateState("createdDate", date?.format('YYYY-MM-DD') ?? "");
                                }}
                                label="Created Date"
                            />
                        </Stack>

                        <Stack gap={4}>
                            <SelectBox
                                isPaginate
                                label={"Reviewer"}
                                loadOptions={loadReviewers}
                                loading={reviewersResponse.isLoading || reviewersResponse.isFetching}
                                options={[]}
                                value={publishState.reviewer || null}
                                onChange={(dat: SelectBoxOption) => {
                                    updateState("reviewer", dat)
                                }}
                                isMenuFixed
                            />

                            <DatePicker
                                value={publishState.reviewedDate ? moment(publishState.reviewedDate, 'YYYY-MM-DD') : null}
                                onChange={(date) => {
                                    updateState("reviewedDate", date?.format('YYYY-MM-DD') ?? "");
                                }}
                                label="Reviewed Date"
                            />
                        </Stack>

                        <Stack gap={4}>
                            <SelectBox
                                isPaginate
                                label={"Approver"}
                                loadOptions={loadApprovers}
                                loading={approversResponse.isLoading || approversResponse.isFetching}
                                options={[]}
                                value={publishState.approver || null}
                                onChange={(dat: SelectBoxOption) => {
                                    updateState("approver", dat)
                                }}
                                isMenuFixed
                            />

                            <DatePicker
                                value={publishState.approvedDate ? moment(publishState.approvedDate, 'YYYY-MM-DD') : null}
                                onChange={(date) => {
                                    updateState("approvedDate", date?.format('YYYY-MM-DD') ?? "");
                                }}
                                label="Approved Date"
                            />
                        </Stack>
                    </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 7 }}>
                    <Stack gap={2}>
                        <Stack direction={"row"}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={publishState.dsChecked}
                                        onChange={() => {
                                            updateState("dsChecked", !publishState.dsChecked);
                                        }}
                                    />
                                }
                                label={"Document Stamp"}
                            />

                            <Button disabled size="small" variant="outlined" startIcon={<Approval />}>Preview</Button>
                        </Stack>

                        {publishState.dsChecked && <PublishReviewTable sequence_id={props.sequence_id} status={publishState.status} tableData={tableData} />}
                    </Stack>
                </Grid>

                <Dialog
                    maxWidth={"sm"}
                    fullWidth={true}
                    open={open}
                    onClose={handleClose}
                    TransitionComponent={Transition}
                >
                    <DependencyChildren isDocument={props.isDocument} object_id={props?.object_id} object_type={props.object_type} onPublish={handleClose} />
                </Dialog>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Stack
                direction={"row"}
                spacing={1}
                sx={{
                    width: "100%",
                    justifyContent: "center",
                    mt: 1,
                    mb: 2
                }}>
                <Button onClick={() => dispatch(closeDialog())} color="primary" variant="outlined">
                    Cancel
                </Button>
                <Button onClick={() => publish(props, publishState)} color="primary" variant="contained" id="publish">
                    Publish
                </Button>
            </Stack>
        </DialogActions>
    </>);
}
